import { CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import type { MeetingRow } from "../types";
import { Card, CardHeader, Empty, SkeletonRow, ViewAllLink } from "./_shared";

type Props = {
  meetings: MeetingRow[];
  isLoading?: boolean;
};

function DateBlock({ iso }: { iso: string }) {
  const d = new Date(iso);
  const day = d.toLocaleDateString("en-GB", { day: "2-digit" });
  const month = d
    .toLocaleDateString("en-GB", { month: "short" })
    .toUpperCase();
  return (
    <div
      className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg text-white"
      style={{ background: "hsl(210,68%,55%)", padding: "4px 8px" }}
    >
      <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1 }}>
        {day}
      </span>
      <span style={{ fontSize: 10, letterSpacing: "0.05em" }}>{month}</span>
    </div>
  );
}

function timeRange(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return time;
}

export function UpcomingMeetingsWidget({ meetings, isLoading }: Props) {
  return (
    <Card>
      <CardHeader
        icon={
          <CalendarDays
            className="h-4 w-4 text-[hsl(262,83%,45%)]"
            strokeWidth={1.5}
          />
        }
        title="Upcoming Meetings"
        count={meetings.length}
        action={<ViewAllLink href="/meetings" />}
      />
      {isLoading ? (
        <div>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonRow key={i} height={48} />
          ))}
        </div>
      ) : meetings.length === 0 ? (
        <Empty
          icon={
            <CalendarDays
              className="h-8 w-8 text-muted-foreground opacity-40"
              strokeWidth={1.5}
            />
          }
          text="No upcoming meetings this week"
        />
      ) : (
        <ul className="space-y-2">
          {meetings.slice(0, 5).map((m) => (
            <li key={m.id}>
              <Link
                to={`/meetings/${m.id}`}
                className="flex items-center gap-3 rounded-md p-1.5 hover:bg-[hsl(210,50%,98%)]"
              >
                <DateBlock iso={m.scheduledAt} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-foreground">
                    {m.title}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {timeRange(m.scheduledAt)}
                    {m.projectName ? ` · ${m.projectName}` : ""}
                  </div>
                </div>
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium"
                  style={{
                    background: "hsl(210,55%,92%)",
                    color: "hsl(210,100%,28%)",
                  }}
                >
                  {m.type ?? m.status}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
